import { can, formatDate } from '@clim/client-core';
import { RESOURCE_CATEGORY, isoDate, uuidv7, type AnnouncementRecord, type ResourceRecord } from '@clim/domain';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';

interface Audiences {
  roles: { code: string; name: string }[];
  territories: { id: string; code: string; name: string }[];
  territoryRequired: boolean;
}

/**
 * « Ressources & Annonces » (§15). Annonces ciblées (épinglées d'abord, non lues signalées, urgentes mises en avant),
 * lisibles hors ligne ; ressources classées par catégorie, datées et versionnées, téléchargeables en ligne.
 * Publication par un administrateur ou un superviseur autorisé.
 */
export function AnnouncementsPage() {
  const { me } = useReady();
  const location = useLocation();
  const [tab, setTab] = useState<'news' | 'resources'>(location.pathname.startsWith('/ressources') ? 'resources' : 'news');
  const engine = useEngine();
  const unread = engine.unreadAnnouncements;
  return (
    <div>
      <h1>Ressources & Annonces</h1>
      <div className="chips" role="tablist">
        <button role="tab" aria-selected={tab === 'news'} className={`chip${tab === 'news' ? ' on' : ''}`} onClick={() => setTab('news')}>
          Annonces{unread ? ` · ${unread} non lue(s)` : ''}
        </button>
        <button role="tab" aria-selected={tab === 'resources'} className={`chip${tab === 'resources' ? ' on' : ''}`} onClick={() => setTab('resources')}>
          Ressources
        </button>
      </div>
      {tab === 'news' ? <News publisher={can(me, 'announcement.publish')} /> : <Resources manager={can(me, 'announcement.publish')} />}
    </div>
  );
}

function useAudiences(enabled: boolean) {
  const { api } = useReady();
  const online = useOnline();
  const [data, setData] = useState<Audiences | null>(null);
  useEffect(() => {
    if (!enabled || !online) return;
    api.request<Audiences>('GET', '/v1/announcements/audiences').then(setData, () => setData(null));
  }, [api, enabled, online]);
  return data;
}

function audienceLabel(a: { audienceRoles: string[]; territoryIds: string[] }, aud: Audiences | null, territories: { id: string; code: string }[]) {
  const roles = a.audienceRoles.length ? a.audienceRoles.map((r) => aud?.roles.find((x) => x.code === r)?.name ?? r).join(', ') : 'Tout le monde';
  const terrs = a.territoryIds.length ? ` · ${a.territoryIds.map((t) => territories.find((x) => x.id === t)?.code ?? '?').join(', ')}` : '';
  return roles + terrs;
}

/* ------------------------------------------------------------------ annonces */

function News({ publisher }: { publisher: boolean }) {
  const engine = useEngine();
  const { me, api } = useReady();
  const online = useOnline();
  const location = useLocation();
  const aud = useAudiences(publisher);
  const territories = engine.list('territory');
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [reads, setReads] = useState<{ id: string; total: number; read: number; users: { name: string; readAt: string | null }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const live = engine.liveAnnouncements(isoDate(new Date()));
  const archived = engine
    .list('announcement')
    .filter((a) => !live.includes(a))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const list = showArchived ? archived : live;
  const focus = location.hash.slice(1);

  // opening the page (or the linked announcement) marks what is shown as read
  useEffect(() => {
    for (const a of live) if (!a.readAt && a.publishedById !== me.user.id && (!focus || focus === a.id)) void engine.markAnnouncementRead(a.id);
  }, [engine, engine.version, focus, me.user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (a: AnnouncementRecord, body: Record<string, unknown>) => {
    setError(null);
    try {
      await api.request('PATCH', `/v1/announcements/${a.id}`, body);
      await engine.sync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const showReads = async (a: AnnouncementRecord) => {
    try {
      setReads({ id: a.id, ...(await api.request<{ total: number; read: number; users: { name: string; readAt: string | null }[] }>('GET', `/v1/announcements/${a.id}/reads`)) });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <div>
      {publisher && (
        <div className="filters">
          <button className="btn primary" onClick={() => setCreating(true)} disabled={!online}>
            + Nouvelle annonce
          </button>
          <label className="check">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Archivées / expirées
          </label>
        </div>
      )}
      <ErrorBox message={error} />
      {!list.length && <Empty>{showArchived ? 'Aucune annonce archivée.' : 'Aucune annonce pour le moment.'}</Empty>}
      <ul className="plain announcements">
        {list.map((a) => {
          const mine = a.publishedById === me.user.id;
          const manage = publisher && (mine || me.permissions['announcement.publish'] === 'all');
          return (
            <li key={a.id} id={a.id} className={`card announcement${a.urgent ? ' urgent' : ''}${!a.readAt && !mine ? ' unread' : ''}${focus === a.id ? ' focus' : ''}`}>
              <div className="card-head">
                <h2>
                  {a.pinned && <span aria-label="Épinglée">📌 </span>}
                  {a.title}
                </h2>
                <span>
                  {a.urgent && <span className="badge tone-danger">Urgent</span>} {!a.readAt && !mine && <span className="badge tone-info">Non lu</span>}
                </span>
              </div>
              <p className="pre">{a.body}</p>
              <p className="muted">
                {a.publishedByName} · {formatDate(a.publishedAt)}
                {a.expiresOn ? ` · jusqu’au ${a.expiresOn.split('-').reverse().join('/')}` : ''}
                {publisher ? ` · ${audienceLabel(a, aud, territories)}` : ''}
              </p>
              {manage && online && (
                <div className="actions">
                  <button className="btn small" onClick={() => void patch(a, { pinned: !a.pinned })}>
                    {a.pinned ? 'Désépingler' : 'Épingler'}
                  </button>
                  <button className="btn small" onClick={() => void patch(a, { archived: !a.archivedAt })}>
                    {a.archivedAt ? 'Désarchiver' : 'Archiver'}
                  </button>
                  <button className="btn small" onClick={() => void showReads(a)}>
                    Lectures
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {creating && aud && <AnnouncementDialog aud={aud} onClose={() => setCreating(false)} />}
      {reads && (
        <Modal title={`Lue par ${reads.read} / ${reads.total}`} onClose={() => setReads(null)}>
          <ul className="plain">
            {reads.users.map((u) => (
              <li key={u.name}>
                {u.readAt ? '✔' : '○'} {u.name} {u.readAt && <span className="muted">· {formatDate(u.readAt)}</span>}
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
}

function AudienceFields({ aud, roles, setRoles, terrs, setTerrs }: { aud: Audiences; roles: string[]; setRoles(v: string[]): void; terrs: string[]; setTerrs(v: string[]): void }) {
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <>
      <Field label="Destinataires" hint="Aucun rôle coché = tout le monde">
        <div className="chips">
          {aud.roles.map((r) => (
            <label key={r.code} className="check">
              <input type="checkbox" checked={roles.includes(r.code)} onChange={() => setRoles(toggle(roles, r.code))} /> {r.name}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Territoires" hint={aud.territoryRequired ? 'Obligatoire : un ou plusieurs de vos territoires' : 'Aucun coché = tous les territoires'}>
        <div className="chips">
          {aud.territories.map((t) => (
            <label key={t.id} className="check">
              <input type="checkbox" checked={terrs.includes(t.id)} onChange={() => setTerrs(toggle(terrs, t.id))} /> {t.name}
            </label>
          ))}
        </div>
      </Field>
    </>
  );
}

function AnnouncementDialog({ aud, onClose }: { aud: Audiences; onClose(): void }) {
  const { api } = useReady();
  const engine = useEngine();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [expiresOn, setExpiresOn] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [terrs, setTerrs] = useState<string[]>(aud.territoryRequired && aud.territories.length === 1 ? [aud.territories[0]!.id] : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.request('POST', '/v1/announcements', { id: uuidv7(), title, body, urgent, pinned, expiresOn: expiresOn || null, audienceRoles: roles, territoryIds: terrs });
      await engine.sync();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Nouvelle annonce" onClose={onClose}>
      <div className="form">
        <Field label="Titre">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </Field>
        <Field label="Message">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} />
        </Field>
        <AudienceFields aud={aud} roles={roles} setRoles={setRoles} terrs={terrs} setTerrs={setTerrs} />
        <label className="check">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> Urgente (notification push + badge « non lu »)
        </label>
        <label className="check">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> Épingler en haut
        </label>
        <Field label="Visible jusqu’au (facultatif)">
          <Input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </Field>
        <ErrorBox message={error} />
        <p className="muted">Une annonce publiée n’est plus modifiable (elle peut être épinglée ou archivée).</p>
        <button className="btn primary" onClick={() => void publish()} disabled={busy || !title.trim() || !body.trim()}>
          Publier
        </button>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ ressources */

const size = (b: number) => (b > 1_048_576 ? `${(b / 1_048_576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);
const dayFr = (d: string) => d.split('-').reverse().join('/');

function Resources({ manager }: { manager: boolean }) {
  const engine = useEngine();
  const { api } = useReady();
  const online = useOnline();
  const aud = useAudiences(manager);
  const territories = engine.list('territory');
  const categories = engine
    .list('reason')
    .filter((r) => r.category === RESOURCE_CATEGORY)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [versionOf, setVersionOf] = useState<ResourceRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const term = q.trim().toLowerCase();
  const list = engine
    .list('resource')
    .filter((r) => r.isActive || manager)
    .filter((r) => !term || [r.title, r.description].some((v) => v?.toLowerCase().includes(term)))
    .sort((a, b) => a.title.localeCompare(b.title));

  // consulter la ressource dans Krotime (affichée depuis le cloud, rien n'est enregistré sur l'appareil)
  const [viewing, setViewing] = useState<{ title: string; url: string; mime: string; versionId: string } | null>(null);
  const view = async (title: string, versionId: string) => {
    setError(null);
    try {
      const r = await api.request<{ url: string; mime: string }>('GET', `/v1/resource-versions/${versionId}/url`);
      setViewing({ title, url: api.resolveUrl(r.url), mime: r.mime, versionId });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const download = async (versionId: string) => {
    setError(null);
    try {
      const { url } = await api.request<{ url: string }>('GET', `/v1/resource-versions/${versionId}/url?download=1`);
      window.open(api.resolveUrl(url), '_blank', 'noopener');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const toggleActive = async (r: ResourceRecord) => {
    try {
      await api.request('PATCH', `/v1/resources/${r.id}`, { isActive: !r.isActive });
      await engine.sync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <div>
      <div className="filters">
        <Input type="search" placeholder="Rechercher une ressource…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Rechercher une ressource" />
        {manager && (
          <button className="btn primary" onClick={() => setCreating(true)} disabled={!online || !aud}>
            + Nouvelle ressource
          </button>
        )}
      </div>
      {!online && <p className="muted">Hors ligne : la liste reste consultable, le téléchargement nécessite une connexion.</p>}
      <ErrorBox message={error} />
      {!list.length && <Empty>Aucune ressource disponible.</Empty>}
      {categories.map((c) => {
        const inCat = list.filter((r) => r.categoryId === c.id);
        if (!inCat.length) return null;
        return (
          <section key={c.id} className="card">
            <h2>{c.label}</h2>
            <ul className="plain resources">
              {inCat.map((r) => {
                const cur = r.versions[0];
                return (
                  <li key={r.id} className={`resource-row${r.isActive ? '' : ' disabled'}`}>
                    <div className="row-main">
                      <strong>{r.title}</strong>
                      {r.description && <span className="muted">{r.description}</span>}
                      {cur && (
                        <span className="muted">
                          Version {cur.versionNo} · datée du {dayFr(cur.validFrom)} · {size(cur.sizeBytes)}
                          {cur.note ? ` · ${cur.note}` : ''}
                        </span>
                      )}
                      {manager && <span className="muted">Pour : {audienceLabel(r, aud, territories)}{r.isActive ? '' : ' · désactivée'}</span>}
                    </div>
                    <div className="actions">
                      {cur && (
                        <button className="btn small primary" onClick={() => void view(r.title, cur.id)} disabled={!online} aria-label={`Voir ${r.title}`}>
                          👁 Voir
                        </button>
                      )}
                      {cur && (
                        <button className="btn small" onClick={() => void download(cur.id)} disabled={!online} aria-label={`Télécharger ${r.title}`}>
                          Télécharger
                        </button>
                      )}
                      {r.versions.length > 1 && (
                        <button className="btn small" onClick={() => setOpen(open === r.id ? null : r.id)}>
                          Versions ({r.versions.length})
                        </button>
                      )}
                      {manager && (
                        <>
                          <button className="btn small" onClick={() => setVersionOf(r)} disabled={!online}>
                            Nouvelle version
                          </button>
                          {cur && (
                            <button className="btn small" onClick={() => void toggleActive(r)} disabled={!online}>
                              {r.isActive ? 'Désactiver' : 'Activer'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {open === r.id && (
                      <ul className="plain versions" aria-label={`Versions de ${r.title}`}>
                        {r.versions.map((v) => (
                          <li key={v.id}>
                            V{v.versionNo} · datée du {dayFr(v.validFrom)} · publiée le {formatDate(v.publishedAt)} par {v.publishedByName}
                            {v.note ? ` · ${v.note}` : ''}{' '}
                            <button className="btn ghost small" onClick={() => void view(`${r.title} (V${v.versionNo})`, v.id)} disabled={!online}>
                              Voir
                            </button>
                            <button className="btn ghost small" onClick={() => void download(v.id)} disabled={!online}>
                              Télécharger
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      {creating && aud && <ResourceDialog aud={aud} categories={categories} onClose={() => setCreating(false)} />}
      {versionOf && <ResourceDialog aud={null} categories={categories} resource={versionOf} onClose={() => setVersionOf(null)} />}
      {viewing && (
        <Modal title={viewing.title} onClose={() => setViewing(null)}>
          <div className="resource-viewer" data-testid="resource-viewer">
            {viewing.mime.startsWith('image/') ? (
              <img src={viewing.url} alt={viewing.title} className="photo-full" />
            ) : viewing.mime === 'application/pdf' ? (
              <iframe src={viewing.url} title={viewing.title} className="resource-frame" />
            ) : viewing.mime.startsWith('video/') ? (
              <video src={viewing.url} controls playsInline className="photo-full" />
            ) : (
              <p className="muted">Aperçu indisponible pour ce type de fichier.</p>
            )}
          </div>
          <div className="actions wrap">
            <a className="btn" href={viewing.url} target="_blank" rel="noreferrer">
              Ouvrir en plein écran
            </a>
            <button className="btn" onClick={() => void download(viewing.versionId)}>
              Télécharger
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ResourceDialog({ aud, categories, resource, onClose }: { aud: Audiences | null; categories: { id: string; label: string }[]; resource?: ResourceRecord; onClose(): void }) {
  const { api } = useReady();
  const engine = useEngine();
  const file = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [roles, setRoles] = useState<string[]>([]);
  const [terrs, setTerrs] = useState<string[]>(aud?.territoryRequired && aud.territories.length === 1 ? [aud.territories[0]!.id] : []);
  const [validFrom, setValidFrom] = useState(isoDate(new Date()));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const f = file.current?.files?.[0];
    if (!f) return setError('Choisissez un fichier (PDF ou image)');
    setBusy(true);
    setError(null);
    try {
      let id = resource?.id;
      if (!id) {
        id = uuidv7();
        await api.request('POST', '/v1/resources', { id, title, description: description || null, categoryId, audienceRoles: roles, territoryIds: terrs });
      }
      const qs = new URLSearchParams({ name: resource?.title ?? title, validFrom, ...(note ? { note } : {}) });
      await api.request('POST', `/v1/resources/${id}/versions?${qs}`, new Uint8Array(await f.arrayBuffer()));
      await engine.sync();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={resource ? `Nouvelle version — ${resource.title}` : 'Nouvelle ressource'} onClose={onClose}>
      <div className="form">
        {!resource && aud && (
          <>
            <Field label="Titre">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
            </Field>
            <Field label="Description (facultative)">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Catégorie">
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </Field>
            <AudienceFields aud={aud} roles={roles} setRoles={setRoles} terrs={terrs} setTerrs={setTerrs} />
          </>
        )}
        <Field label="Fichier (PDF ou image)">
          <input ref={file} type="file" accept="application/pdf,image/*" aria-label="Fichier" />
        </Field>
        <Field label="Date du document">
          <Input type="date" value={validFrom} onChange={(e) => e.target.value && setValidFrom(e.target.value)} />
        </Field>
        <Field label="Note de version (facultative)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex. prix mis à jour" />
        </Field>
        <ErrorBox message={error} />
        {resource && <p className="muted">La nouvelle version remplace l’ancienne : seul le document le plus récent est conservé.</p>}
        <button className="btn primary" onClick={() => void save()} disabled={busy || (!resource && !title.trim())}>
          Publier
        </button>
      </div>
    </Modal>
  );
}
